import { apiFetch, serviceBase } from './http';

/* ПОЛУЧЕНИЕ ПЛАНА ПО ССЫЛКЕ — `post-care-api`, пациентская половина.

       POST {gw}/post-care-api.application/api/v1/write/patient/post-care/claim

   Врач нажал «отправить план пациенту», сервер выдал секрет и напечатал адрес
   `<care_link_root><секрет>`; пациент открывает его в браузере, входит по
   номеру, и ЭТА ручка сверяет, что номер аккаунта совпал с номером в карте. Она
   же пишет получателя (`claimed_user_id`) и связывает карту с аккаунтом.

   СЕКРЕТ УХОДИТ ТЕЛОМ, А НЕ В QUERY — требование сервера и довод у него точный:
   query-строки оседают в логах доступа, в аудите и в `Referer`. По той же
   причине он не попадает ни в один лог кабинета и не живёт в состоянии дольше
   одного вызова.

   ПОЧЕМУ ОТКАЗЫ РАЗВЕДЕНЫ ПО ТЕКСТАМ. Сервер различает пять исходов, и склеивать
   их в «не удалось» нельзя: пациенту нужно понять, ждать ему, просить новую
   ссылку или звонить врачу. Единственное, чего он различать НЕ должен, — было ли
   вообще такое звено: 404 отвечает одним телом на «секрета нет», «план убран»,
   «ссылку забрал другой аккаунт» и «карты плана не существует». */

export type ClaimResult =
    /** Ссылка забрана: план открыт этому аккаунту навсегда. */
    | { state: 'ok'; channelId: string }
    /** Нет заголовка личности — пациент не вошёл. Не отказ, а шаг флоу. */
    | { state: 'anonymous' }
    /** Секрета нет, план убран, или ссылку уже забрал другой аккаунт. */
    | { state: 'gone' }
    /** Срок вышел ИЛИ ссылку погасила повторная выдача врачом. */
    | { state: 'expired' }
    /** Номер аккаунта не совпал с номером в карте, либо карта занята другим. */
    | { state: 'mismatch' }
    /** Сверять нечем: номера нет у аккаунта или нет на карте. */
    | { state: 'nophone' }
    /** Сосед не ответил — сверка не состоялась, ссылка осталась незабранной. */
    | { state: 'neighbour' }
    | { state: 'offline' }
    | { state: 'error' };

export async function claimLink(token: string): Promise<ClaimResult> {
    const base = serviceBase('post-care-api');
    if (base === null) return { state: 'offline' };
    try {
        const res = await apiFetch(base, '/write/patient/post-care/claim', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token }),
        });
        if (res.status === 401) return { state: 'anonymous' };
        if (res.status === 404) return { state: 'gone' };
        if (res.status === 410) return { state: 'expired' };
        if (res.status === 409) return { state: 'mismatch' };
        if (res.status === 422) return { state: 'nophone' };
        if (res.status === 502) return { state: 'neighbour' };
        if (!res.ok) return { state: 'error' };
        const body = (await res.json()) as { channelId?: unknown };
        const id = typeof body?.channelId === 'string' ? body.channelId : '';
        // Ответ несёт АДРЕС экрана, а не ключ к нему: секрета у сервера больше
        // нет, в базе лежит только его sha256. Без id открывать нечего.
        return id ? { state: 'ok', channelId: id } : { state: 'error' };
    } catch {
        // Сюда же попадает отказ CORS — из браузера он неотличим от обрыва сети.
        return { state: 'error' };
    }
}

/* ТОКЕН ЧЕРЕЗ ВХОД. Keycloak возвращает пациента на `/login`, а не туда, откуда
   он ушёл, — адрес возврата обязан совпасть с разрешённым у клиента, и вторым он
   быть не может. Значит секрет надо пронести через редирект самим.

   `sessionStorage`, а не `localStorage`: он умирает вместе с вкладкой. Секрет,
   переживший закрытие браузера на чужом компьютере, — это выданный медицинский
   документ, лежащий и ждущий. По той же причине `takePending` СТИРАЕТ значение
   при чтении: оно нужно ровно один раз.

   Хранилище может быть недоступно (приватный режим, запрет на данные сайта) —
   тогда обе функции молча ничего не делают, и пациент после входа просто
   откроет ссылку из сообщения врача второй раз. Это хуже на один шаг, но не
   ломает ничего. */
const PENDING = 'aventrae.care.pending';

export function stashPending(token: string): void {
    try {
        window.sessionStorage.setItem(PENDING, token);
    } catch {
        /* приватный режим — переживём */
    }
}

export function takePending(): string | null {
    try {
        const v = window.sessionStorage.getItem(PENDING);
        if (v) window.sessionStorage.removeItem(PENDING);
        return v || null;
    } catch {
        return null;
    }
}

/* ОДНА АВТОМАТИЧЕСКАЯ ПОПЫТКА ВХОДА НА СЕКРЕТ (страховка от петли, 16.09).

   Экран приёма ссылки уходит на вход САМ, без клика. Если после возврата ручка
   снова ответит «не вошёл», второй такой уход закрутит петлю: живая сессия
   Keycloak возвращает мгновенно и ничего не спрашивает, а отложенный секрет
   несёт человека обратно на этот же экран. Вкладка будет крутиться, пока её не
   закроют. Условие редкое — `read/me` говорит «вошёл», а `claim` отвечает 401, —
   но достижимое, и цена ошибки тут неприятнее самой ошибки.

   Поэтому: первый раз уходим молча, второй — показываем экран с кнопкой. Решение
   принимает человек, а не редирект.

   Хранилища может не быть (приватный режим) — тогда попытка и так единственная:
   без `PENDING` возврат с входа приводит в кабинет, а не сюда. */
const TRIED = 'aventrae.care.tried';

/** Можно ли уходить на вход за этим секретом. Отвечает «да» ровно один раз. */
export function loginOnce(token: string): boolean {
    try {
        if (window.sessionStorage.getItem(TRIED) === token) return false;
        window.sessionStorage.setItem(TRIED, token);
        return true;
    } catch {
        return true;
    }
}

/** Секрет забран — отметка больше не нужна. Без этого повторное открытие своей
 *  же ссылки в той же вкладке после протухшей сессии показало бы экран с
 *  кнопкой вместо привычного молчаливого входа. */
export function forgetLoginTry(): void {
    try {
        window.sessionStorage.removeItem(TRIED);
    } catch {
        /* приватный режим — переживём */
    }
}
